<script setup lang="ts">
import { watch } from "vue";
import { findAddressRules } from "@/validation";
import useVuelidate, { type ErrorObject } from "@vuelidate/core";

import Question from "@/components/general/Question/Question.vue";
import TextField from "@/components/general/TextField/TextField.vue";
import Button from "@/components/general/Button/Button.vue";
import Dropdown from "@/components/general/Dropdown/Dropdown.vue";
import Icon from "@/components/general/Icon/Icon.vue";
import validIconUrl from "@/assets/img/tick.png";

import { useCustomerStore, useDataLayerStore, useAddressStore } from "@/stores";
import { EventTypeEnum } from "@/models";

import { storeToRefs } from "pinia";

const dataLayer = useDataLayerStore();
const customer = useCustomerStore();
const addressStore = useAddressStore();
const v$ = useVuelidate(findAddressRules(), customer.address);

const { selectedAddress } = storeToRefs(customer);

const getErrorMessage = (errors: ErrorObject[] | undefined): string => {
    const message = errors?.[0]?.$message;
    return typeof message === "string" ? message : "";
};

const detailChanged = (key: string) => {
    v$.value[key].$touch();
    if (v$.value[key].$error) {
        dataLayer.errors = v$.value.$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, key);
    }
    dataLayer.triggerEvent(EventTypeEnum.QuestComplete, key);
};

const showManualAddressHandler = () => {
    addressStore.manualAddress = true;
    addressStore.showAddress = false;
    addressStore.loadingAddress = false;
};

const hideManualAddressHandler = () => {
    addressStore.manualAddress = false;
    addressStore.changeAddress();
};

const submitManualEnter = async () => {
    v$.value.$touch();
    const valid = await v$.value.$validate();
    if (!valid) {
        dataLayer.errors = v$.value.$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, "manualAddress");
        return;
    }

    selectedAddress.value = null;
    addressStore.manualAddress = false;
    addressStore.showAddress = true;
};

const changeAddressHandler = () => {
    v$.value.postcode.$reset();
    addressStore.changeAddress();
};

const searchPostcode = () => {
    v$.value.postcode.$validate().then((valid: boolean) => {
        if (valid) {
            addressStore.triggerPostcodeLookup();
        }
    });
};

watch(selectedAddress, () => {
    if (selectedAddress.value) {
        addressStore.grabAddress();
    }
});
</script>

<template>
    <div v-if="!addressStore.manualAddress && !addressStore.showAddress">
        <Question
            id="your-details-find-address-question"
            label="Your Postcode:*"
            :error-message="getErrorMessage(v$.postcode?.$errors)"
            class="postcode-width"
        >
            <TextField
                id="your-details-postcode"
                v-model="customer.address.postcode"
                type="text"
                placeholder="Postcode"
                :maxlength="10"
                :has-error="v$.postcode.$error"
                @blur="detailChanged('postcode')"
            />
        </Question>

        <div
            v-if="
                addressStore.showAddressDropdown &&
                !addressStore.manualAddress &&
                !addressStore.showAddress
            "
            class="result-section"
        >
            <Dropdown
                :id="'addressDropdown'"
                v-model="selectedAddress"
                class="dropdown"
                :options="addressStore.formattedAddresses"
                placeholder="Please select"
                :full-width="true"
                @change="
                    dataLayer.triggerEvent(
                        EventTypeEnum.QuestComplete,
                        'selectedAddress'
                    )
                "
            />
        </div>

        <div class="button-container">
            <Button
                :id="'Find-Address-button'"
                btn-type="primary"
                @click.prevent="searchPostcode"
            >
                Find Address
            </Button>
            <Button
                :id="'Manual-Address-button'"
                btn-type="tertiary"
                @click.prevent="showManualAddressHandler"
            >
                Enter address manually
            </Button>
        </div>
    </div>

    <div v-if="addressStore.manualAddress">
        <div class="manual-header-container">
            <div class="manual-header-content">
                <h3>Enter your Address</h3>
                <div>
                    <p
                        >Please complete the following information to help us
                        find your address</p
                    >
                </div>
            </div>
            <Icon
                class="close-manual"
                name="close"
                type="png"
                @click="hideManualAddressHandler"
            />
        </div>

        <Question
            id="your-details-address-line-1-question"
            label="House name or no:*"
            :error-message="getErrorMessage(v$.line1?.$errors)"
            class="cap-width"
        >
            <TextField
                id="your-details-line-1"
                v-model="customer.address.line1"
                type="text"
                placeholder="Line 1"
                :maxlength="24"
                :has-error="v$.line1.$error"
                @blur="detailChanged('line1')"
            />
        </Question>

        <Question
            id="your-details-address-line-2-question"
            label="Street:"
            :error-message="getErrorMessage(v$.line2?.$errors)"
            class="cap-width"
        >
            <TextField
                id="your-details-line-2"
                v-model="customer.address.line2"
                type="text"
                placeholder="Line 2"
                :maxlength="24"
                :has-error="v$.line2.$error"
                @blur="detailChanged('line2')"
            />
        </Question>

        <Question
            id="your-details-address-line-3-question"
            label="Area:"
            :error-message="getErrorMessage(v$.line3?.$errors)"
            class="cap-width"
        >
            <TextField
                id="your-details-line-3"
                v-model="customer.address.line3"
                type="text"
                placeholder="Line 3"
                :maxlength="24"
                :has-error="v$.line3.$error"
                @blur="detailChanged('line3')"
            />
        </Question>

        <Question
            id="your-details-address-line-4-question"
            label="Town or City:*"
            :error-message="getErrorMessage(v$.line4?.$errors)"
            class="cap-width"
        >
            <TextField
                id="your-details-line-4"
                v-model="customer.address.line4"
                type="text"
                placeholder="Line 4"
                :maxlength="24"
                :has-error="v$.line4.$error"
                @blur="detailChanged('line4')"
            />
        </Question>

        <Question
            id="your-details-address-line-5-question"
            label="County:"
            :error-message="getErrorMessage(v$.line5?.$errors)"
            class="cap-width"
        >
            <TextField
                id="your-details-line-5"
                v-model="customer.address.line5"
                type="text"
                placeholder="Line 5"
                :maxlength="24"
                :has-error="v$.line5.$error"
                @blur="detailChanged('line5')"
            />
        </Question>

        <Question
            id="your-details-address-postcode-question"
            label="Postcode:*"
            :error-message="getErrorMessage(v$.postcode?.$errors)"
            class="postcode-width"
        >
            <TextField
                id="your-details-postcode-manual"
                v-model="customer.address.postcode"
                type="text"
                placeholder="Postcode"
                :maxlength="10"
                :has-error="v$.postcode.$error"
                @blur="detailChanged('postcode')"
            />
        </Question>

        <div class="manual-address-button-container">
            <Button
                :id="'cancel-manual-address-button'"
                btn-type="secondary"
                @click.prevent="hideManualAddressHandler"
            >
                Cancel
            </Button>
            <Button
                :id="'use-this-address-button'"
                btn-type="primary"
                @click.prevent="submitManualEnter"
            >
                Use this address
            </Button>
        </div>
    </div>
    <div v-if="addressStore.showAddress">
        <div class="inputted-address">
            <div>
                <p>
                    {{ customer.address.line1 }}
                    {{
                        customer.address.line2
                            ? ", " + customer.address.line2
                            : ""
                    }}
                </p>
                <p>{{ customer.address.line3 }}</p>
                <p>{{ customer.address.line4 }}</p>
                <p>{{ customer.address.line5 }}</p>
                <p>{{ customer.address.postcode }}</p>
            </div>
            <div class="inputted-address-right-container">
                <img
                    class="inputted-address-tick"
                    :src="validIconUrl"
                    alt="tick"
                />
                <Button
                    :id="'change-address-button'"
                    btn-type="secondary"
                    @click.prevent="changeAddressHandler"
                >
                    Change Address
                </Button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.manual-header-container {
    display: flex;
    justify-content: space-between;
}

.manual-address-button-container {
    display: flex;
    justify-content: space-between;
}

.inputted-address-right-container {
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: space-between;
}

.inputted-address-tick {
    width: 28px;
    height: 28px;
}

.button-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 6.625rem;
}

.result-section {
    max-width: 33.75rem;
    margin-bottom: 1rem;
}

.inputted-address {
    background-color: var(--selection-background);
    border: 1px solid var(--color-primary-light);
    padding: 1.25rem;
    display: flex;
    justify-content: space-between;

    p {
        margin-bottom: 0.25rem;
    }
}

.close-manual {
    max-width: 20px;
    max-height: 20px;
    cursor: pointer;
}

.cap-width {
    max-width: 33.75rem;
}

.postcode-width {
    max-width: 17.5rem;
}

.text-field {
    width: 100%;
}

@media screen and (width <= 585px) {
    .postcode-width {
        max-width: initial;
    }
}
</style>
